import React, { useState, useEffect, useRef } from 'react';
import { Bell } from 'lucide-react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../lib/firebase';
import { subscribeToNotifications, getNotifications } from '../../lib/notificationStore';
import { Notification } from '../../types/notifications';
import { NotificationPanel } from './NotificationPanel';

export function NotificationBell() {
  const [user] = useAuthState(auth);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showPanel, setShowPanel] = useState(false);
  const bellRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      return;
    }

    console.log('Setting up notification subscription in NotificationBell');

    // Fetch notifications immediately on mount
    getNotifications().then(initialNotifications => {
      console.log('Initial notifications loaded:', initialNotifications.length);
      setNotifications(initialNotifications);
    });

    // Then set up realtime subscription for updates
    const unsubscribe = subscribeToNotifications((newNotifications) => {
      console.log('NotificationBell received update:', newNotifications.length, 'notifications');
      setNotifications(prev => {
        const changed = prev.length !== newNotifications.length ||
          JSON.stringify(prev) !== JSON.stringify(newNotifications);
        if (changed) {
          console.log('Updating notifications state');
          return newNotifications;
        }
        return prev;
      });
    });

    return () => {
      console.log('Cleaning up notification subscription');
      unsubscribe();
    };
  }, [user]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (bellRef.current && !bellRef.current.contains(event.target as Node)) {
        setShowPanel(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const unreadCount = notifications.filter(n => !n.read).length;
  console.log('Current unread count:', unreadCount);

  return (
    <div ref={bellRef} className="relative">
      <button
        onClick={() => setShowPanel(!showPanel)}
        className="p-2 rounded-full hover:bg-white relative"
        aria-label={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
      >
        <Bell className="w-5 h-5 text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {showPanel && (
        <NotificationPanel
          notifications={notifications}
          onClose={() => setShowPanel(false)}
          onNotificationsUpdate={setNotifications}
        />
      )}
    </div>
  );
}